<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-top">
          <!-- PC端：网格布局 -->
          <div class="footer-nav footer-nav-pc">
            <template
              v-for="menu in menuData"
              :key="menu.path">
              <template v-if="!menu.languages || (menu.languages.length && menu.languages.includes(locale))">
                 <div
                    class="nav-col"
                    v-if="menu.path !== '/'"
                  >
                    <h4 class="nav-title">{{ t(menu.i18nKey) }}</h4>
                    <template
                      v-for="child in menu.children"
                      :key="child.path">
                      <a
                        class="nav-link"
                        v-if="!child.languages || (child.languages.length && child.languages.includes(locale))"
                        @click="handleNavClick(child.path)"
                      >
                        {{ t(child.i18nKey) }}
                      </a>
                    </template>
                    <a
                      v-if="!menu.children || menu.children.length === 0"
                      :key="menu.path"
                      class="nav-link"
                      @click="handleNavClick(menu.path)"
                    >
                      {{ t(menu.i18nKey) }}
                    </a>
                  </div>
              </template>
            </template>
          </div>

          <!-- 移动端：两列布局 -->
          <div class="footer-nav-mobile">
            <div class="mobile-nav-left">
              <div
                v-for="(menu, index) in filteredMenuData"
                :key="menu.path"
                class="mobile-menu-item"
                :class="{ 'active': activeMenuIndex === Number(index) }"
                @click="handleMenuSelect(Number(index))"
              >
                <span class="menu-name">{{ t(menu.i18nKey) }}</span>
                <el-icon v-if="menu.children && menu.children.length > 0" class="menu-arrow">
                  <ArrowRight />
                </el-icon>
              </div>
            </div>
            <div class="mobile-nav-right">
              <div v-if="currentSubMenu && currentSubMenu.length > 0" class="sub-menu-list">
                <template v-for="child in currentSubMenu" :key="child.path">
                  <a
                    v-if="child && (!child.languages || (child.languages.length && child.languages.includes(locale)))"
                    class="sub-menu-link"
                    @click="handleNavClick(child.path)"
                  >
                    {{ t(child.i18nKey) }}
                  </a>
                </template>
              </div>
              <div v-else-if="activeMenuIndex !== null && filteredMenuData[activeMenuIndex]" class="sub-menu-list">
                <a
                  class="sub-menu-link"
                  @click="handleNavClick(filteredMenuData[activeMenuIndex].path)"
                >
                  {{ t(filteredMenuData[activeMenuIndex].i18nKey) }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- 版权区域 -->
        <div class="footer-copyright">
          <div class="copyright-info">
            <p class="copyright-text">{{ t('footer.copyright', { year: currentYear }) }}</p>
            <p class="company-info">{{ t('footer.company_info') }}</p>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowRight } from '@element-plus/icons-vue';
import { menuData } from '@/mock/data';
import { useRouter, useRoute } from 'vue-router';

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const activeMenuIndex = ref<number>(0);

const filteredMenuData = computed(() => {
  return menuData.filter((menu: any) => 
    menu.path !== '/' && 
    (!menu.languages || (menu.languages.length && menu.languages.includes(locale.value)))
  );
});

const currentSubMenu = computed(() => {
  const menu = filteredMenuData.value[activeMenuIndex.value];
  if (!menu || !menu.children || menu.children.length === 0) return [];
  return menu.children.filter((child: any) => 
    child && (!child.languages || (child.languages.length && child.languages.includes(locale.value)))
  );
});

const initActiveMenu = () => {
  const currentPath = route.path;
  for (let i = 0; i < filteredMenuData.value.length; i++) {
    const menu = filteredMenuData.value[i];
    if (menu.path === currentPath) {
      activeMenuIndex.value = i;
      return;
    }
    if (menu.children && menu.children.length > 0) {
      const found = menu.children.some((child: any) => {
        const childPath = child.path.split('?')[0];
        return currentPath === childPath || currentPath.startsWith(childPath + '/');
      });
      if (found) {
        activeMenuIndex.value = i;
        return;
      }
    }
  }
  activeMenuIndex.value = 0;
};

onMounted(() => {
  initActiveMenu();
});

const handleMenuSelect = (index: number) => {
  activeMenuIndex.value = index;
};

const handleNavClick = (path: string) => {
  sessionStorage.removeItem('news_list_state');
  sessionStorage.removeItem('notice_list_state');
  router.push(path);
};

const currentYear = new Date().getFullYear();
</script>

<style scoped lang="scss">
@import '@/assets/css/common';

.footer {
  background: linear-gradient(135deg, $gray-80 0%, $primary-dark-4 100%);
  color: $white;
  padding: 60px 0 20px;
  font-family: $font-base;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba($primary-color, 0.3), transparent);
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.footer-top {
  grid-template-columns: 1fr 4fr;
  gap: 60px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

// PC端导航区域
.footer-nav-pc {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    display: none;
  }
}

.nav-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  a {
    cursor: pointer;
  }
}

.nav-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: $white;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba($primary-color, 0.2);
}

.nav-link {
  color: $primary-light-3;
  text-decoration: none;
  font-size: 0.9375rem;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;
  display: inline-block;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background: $primary-color;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover,
  &.router-link-active {
    color: $white;
    padding-left: 12px;

    &::before {
      opacity: 1;
    }
  }

  &.router-link-active {
    font-weight: 500;
  }
}

// 移动端两列布局
.footer-nav-mobile {
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: 20px;
    min-height: 280px;
  }
}

.mobile-nav-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 15px;
  border-right: 1px solid rgba($primary-color, 0.2);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;

  &:hover {
    background: rgba($primary-color, 0.1);
  }

  &.active {
    background: rgba($primary-color, 0.2);

    .menu-name {
      color: $white;
      font-weight: 600;
    }

    .menu-arrow {
      color: $primary-color;
      transform: rotate(0deg);
    }
  }

  .menu-name {
    font-size: 0.9375rem;
    color: $primary-light-3;
    transition: color 0.3s ease;
  }

  .menu-arrow {
    font-size: 14px;
    color: $primary-light-4;
    transition: all 0.3s ease;
  }
}

.mobile-nav-right {
  display: flex;
  flex-direction: column;
  padding-left: 15px;
}

.sub-menu-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sub-menu-link {
  display: block;
  padding: 10px 16px;
  color: $primary-light-3;
  text-decoration: none;
  font-size: 0.875rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  background: transparent;

  &:hover {
    color: $white;
    background: rgba($primary-color, 0.15);
    padding-left: 20px;
  }
}

// 版权区域
.footer-copyright {
  border-top: 1px solid rgba($primary-color, 0.2);
  padding-top: 20px;
  display: flex;
  justify-content: end;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
}

.copyright-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.copyright-text {
  font-size: 0.875rem;
  color: $primary-light-4;
  margin: 0;
}

.company-info {
  font-size: 0.75rem;
  color: $primary-light-6;
  margin: 0;
  opacity: 0.8;
}

// 响应式调整
@media (max-width: 1200px) {
  .container {
    padding: 0 30px;
  }
}

@media (max-width: 992px) {
  .footer {
    padding: 50px 0 20px;
  }

  .footer-content {
    gap: 40px;
  }

  .footer-top {
    gap: 40px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .footer {
    padding: 40px 0 20px;
  }

  .footer-content {
    gap: 30px;
  }

  .nav-title {
    font-size: 1rem;
  }

  .nav-link {
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 16px;
  }

  .footer {
    padding: 30px 0 16px;
  }

  .mobile-menu-item {
    padding: 10px 12px;

    .menu-name {
      font-size: 0.875rem;
    }
  }

  .sub-menu-link {
    padding: 8px 12px;
    font-size: 0.8125rem;
  }
}
</style>
